import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  
  const serverUrl = process.env.REACT_APP_API_SERVER_URL;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  const securedRequest = useCallback(async (method: string, endpoint: string, body: any = null, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    let responseData: { data: any; error: any } = { data: null, error: null };

    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: audience,
        },
      });

      const config: { headers: any, signal: AbortSignal | undefined } = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        signal: signal,
      };

      if (!(body instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }

      const url = `${serverUrl}${endpoint}`;
      let response;

      switch (method.toLowerCase()) {
        case 'get':
          response = await axios.get(url, { ...config, params: body });
          break;
        case 'post':
          response = await axios.post(url, body, config);
          break;
        case 'delete':
          response = await axios.delete(url, config);
          break;
        case 'put':
          response = await axios.put(url, body, config);
          break;
        case 'patch':
          response = await axios.patch(url, body, config);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
      
      responseData.data = response.data;
    } catch (e: unknown) {
      if (axios.isCancel(e)) {
        console.log('Request canceled:', e.message);
        responseData.error = e.message;
      } else if (axios.isAxiosError(e)) {
        console.error('Axios error:', e.message);
        responseData.error = e.response ? e.response.data : e.message;
        setError(responseData.error);
      } else {
        console.error('Unexpected error:', e);
        responseData.error = 'An unexpected error occurred.';
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
    
    return responseData;
  }, [audience, getAccessTokenSilently, serverUrl]);

  return { loading, error, securedRequest };
};

export default useApi;
